import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardHeader, 
  Divider, 
  Link, 
  List, 
  ListItem, 
  ListItemText, 
  Skeleton, 
  CircularProgress,
  TextField, 
  Typography,
  Switch,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Badge
} from '@mui/material';
import { useAuth } from '../../../hooks/use-auth';
import { userApi } from '../../../api/user-api';

export const CustomerBasicDetails = (props) => {
  let { customer, setCustomer } = props;
  const { user } = useAuth(); 
  const navigate = useNavigate();
  const [modify, setModify] = useState({
    "firstname": null,
    "lastname": null,
    "email": null,
    "tlms_id": null,
    "syna_id": null
  });
  const [modifyText, setModifyText] = useState("");
  const [allGroups, setAllGroups] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Charger tous les groupes disponibles et les groupes de l'utilisateur
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        // Récupérer tous les groupes
        const allGroupsResponse = await userApi.getGroups(user.admin_id);
        setAllGroups(allGroupsResponse);
        
        // Récupérer les groupes de l'utilisateur
        if (customer?.details?.id) {
          const userGroupsResponse = await userApi.getUserGroups(user.admin_id, customer.details.id);
          setUserGroups(userGroupsResponse);
          
          // Pré-sélectionner les groupes de l'utilisateur
          setSelectedGroups(userGroupsResponse);
        }
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setLoadingGroups(false);
      }
    };
    
    fetchGroups();
  }, [customer, user.admin_id]);

  function handleClick(field, value) {
    setModify(modify => ({
      ...modify,
      [field]: true})
    );
    setModifyText(value);
  }

  async function handleModify(field) {
    setModify(modify => ({
      ...modify,
      [field]: false})
    );
    let changeCustomer = { ...customer };
    changeCustomer.details[field] = modifyText;
    setCustomer(changeCustomer);
    await userApi.modifyUser(user.admin_id, changeCustomer);
  }

  async function handleModifyRelaunch(relaunche) {
    let changeCustomer = { ...customer };
    changeCustomer.details["auto_relaunch"] = relaunche;
    setCustomer(changeCustomer);
    await userApi.modifyUser(user.admin_id, changeCustomer);
  }

  const handleGroupChange = async () => {
    try {
      // D'abord retirer l'utilisateur de tous ses groupes actuels
      for (const group of userGroups) {
        await userApi.removeUserFromGroup(user.admin_id, customer.details.id, group.id);
      }
      
      // Puis l'ajouter aux nouveaux groupes sélectionnés
      for (const group of selectedGroups) {
        await userApi.assignUserToGroup(user.admin_id, customer.details.id, group.id);
      }
      
      // Mettre à jour l'état local
      setUserGroups(selectedGroups);
      
      // Fermer le dialogue
      setOpenGroupDialog(false);
      
    } catch (error) {
      console.error("Error updating user groups:", error);
    }
  };

  return (
    <>
      {customer === null ? (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    height: 300  // Ajustez la hauteur selon vos besoins
  }}>
    <CircularProgress />
  </Box>
) : (
        <Card>
          <CardHeader title="Informations"/>
          <Divider/>
          <List disablePadding>
            <ListItem
              sx={{
                px: 3,
                py: 1.5
              }}
            >
              <ListItemText
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  my: 0
                }}
                disableTypography
                primary={
                  <Typography
                    sx={{ minWidth: 180}}
                    variant="subtitle2"
                  >
                    Prénom
                  </Typography>
                }
                secondary={(
                  <Box
                    sx={{
                      flex: 1,
                      mt: 0,
                      display: "flex",
                      maxWidth: 1000
                    }}
                  >
                    {modify.firstname ?
                    <TextField
                      size='small'
                      focused
                      autoFocus
                      defaultValue={customer.details.firstname}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleModify("firstname");
                        }
                      }}
                      onBlur={() => {
                        handleModify("firstname");
                      }}
                      onChange={(e) => setModifyText(e.target.value)}
                    />
                    : // ELSE
                    <Typography
                      onDoubleClick={() => handleClick('firstname', customer.details.firstname)}
                      color="textSecondary"
                      variant="body2"
                    >
                      {customer.details.firstname}
                    </Typography>
                    }
                  </Box>
                )}
              />
            </ListItem>
            <Divider/>
            <ListItem
              sx={{
                px: 3,
                py: 1.5
              }}
            >
              <ListItemText
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  my: 0
                }}
                disableTypography
                primary={
                  <Typography
                    sx={{ minWidth: 180}}
                    variant="subtitle2"
                  >
                    Nom
                  </Typography>
                }
                secondary={(
                  <Box
                    sx={{
                      flex: 1,
                      mt: 0,
                      display: "flex",
                      maxWidth: 1000
                    }}
                  >
                    {modify.lastname ?
                    <TextField
                      size='small'
                      focused
                      autoFocus
                      defaultValue={customer.details.lastname}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleModify("lastname");
                        }
                      }}
                      onBlur={() => {
                        handleModify("lastname");
                      }}
                      onChange={(e) => setModifyText(e.target.value)}
                    />
                    : // ELSE
                    <Typography
                      onDoubleClick={() => handleClick('lastname', customer.details.lastname)}
                      color="textSecondary"
                      variant="body2"
                    >
                      {customer.details.lastname}
                    </Typography>
                    }
                  </Box>
                )}
              />
            </ListItem>
            <Divider/>
            <ListItem
              sx={{
                px: 3,
                py: 1.5
              }}
            >
              <ListItemText
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  my: 0
                }}
                disableTypography
                primary={
                  <Typography
                    sx={{ minWidth: 180}}
                    variant="subtitle2"
                  >
                    Email
                  </Typography>
                }
                secondary={(
                  <Box
                    sx={{
                      flex: 1,
                      mt: 0,
                      display: "flex",
                      maxWidth: 1000
                    }}
                  >
                    {modify.email ?
                    <TextField
                      size='small'
                      focused
                      autoFocus
                      defaultValue={customer.details.email}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleModify("email");
                        }
                      }}
                      onBlur={() => {
                        handleModify("email");
                      }}
                      onChange={(e) => setModifyText(e.target.value)}
                    />
                    : // ELSE
                    <Typography
                      onDoubleClick={() => handleClick('email', customer.details.email)}
                      color="textSecondary"
                      variant="body2"
                    >
                      {customer.details.email}
                    </Typography>
                    }
                  </Box>
                )}
              />
            </ListItem>
            <Divider/>
            {user.id_service === 0 && <>
            
            

{customer.tlms_domain&& <> <ListItem
              sx={{
                px: 3,
                py: 1.5
              }}
            >
              <ListItemText
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  my: 0
                }}
                disableTypography
                primary={
                  <Typography
                    sx={{ minWidth: 180}}
                    variant="subtitle2"
                  >
                    Tlms
                  </Typography>
                }
                secondary={(
                  <Box
                    sx={{
                      flex: 1,
                      mt: 0,
                      display: "flex",
                      maxWidth: 1000
                    }}
                    onDoubleClick={() => handleClick('tlms_id', customer.details.tlms_id)}
                  >
                    {modify.tlms_id ?
                    <TextField
                      size='small'
                      focused
                      autoFocus
                      defaultValue={customer.details.tlms_id}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleModify("tlms_id");
                        }
                      }}
                      onBlur={() => {
                        handleModify("tlms_id");
                      }}
                      onChange={(e) => setModifyText(e.target.value)}
                    />
                    : // ELSE
                    <Link
                      href={`https://${customer.tlms_domain}/user/info/id:${customer.details.tlms_id}`}
                      target='_blank'
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography
                        color="textSecondary"
                        variant="body2"
                        >
                        {customer.details.tlms_id === 0 ? "Pas de compte TLMS" : customer.details.tlms_id}
                      </Typography>
                    </Link>
                    }
                  </Box>
                )}
              />
            </ListItem>
            <Divider/></>}
            { customer.has_syna ==='true' &&   <>  <ListItem
              sx={{
                px: 3,
                py: 1.5
              }}
            >
              <ListItemText
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  my: 0
                }}
                disableTypography
                primary={
                  <Typography
                    sx={{ minWidth: 180}}
                    variant="subtitle2"
                  >
                    Syndays
                  </Typography>
                }
                secondary={(
                  <Box
                    sx={{
                      flex: 1,
                      mt: 0,
                      display: "flex",
                      maxWidth: 1000
                    }}
                    onDoubleClick={() => handleClick('syna_id', customer.details.syna_id)}
                  >
                    {modify.syna_id ?
                    <TextField
                      size='small'
                      focused
                      autoFocus
                      defaultValue={customer.details.syna_id}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleModify("syna_id");
                        }
                      }}
                      onBlur={() => {
                        handleModify("syna_id");
                      }}
                      onChange={(e) => setModifyText(e.target.value)}
                    />
                    : // ELSE
                    <Link
                      href={`https://${globalThis.localStorage.getItem('tenant')}.syndays.fr/dashboard/customers/${customer.details.syna_id}`}
                      target='_blank'
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography
                        color="textSecondary"
                        variant="body2"
                        >
                        {customer.details.syna_id === 0 ? "Pas de compte SYNDAYS" : customer.details.syna_id}
                      </Typography>
                    </Link>
                    }
                  </Box>
                )}
              />
            </ListItem><Divider/> </>}
            
            </>
            
            }
            
            <ListItem
              sx={{
                px: 3,
                py: 1.5
              }}
            >
              <ListItemText
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  my: 0
                }}
                disableTypography
                primary={
                  <Typography
                    sx={{ minWidth: 180}}
                    variant="subtitle2"
                  >
                    Dernière activité
                  </Typography>
                }
                secondary={(
                  <Box
                    sx={{
                      flex: 1,
                      mt: 0,
                      display: "flex",
                      maxWidth: 1000
                    }}
                  >
                    <Typography
                      color="textSecondary"
                      variant="body2"
                    >
                      {customer.details.last_activity_type ? customer.details.last_activity_type + " le " + customer.details.last_activity_date : "Aucune activité"}
                    </Typography>
                  </Box>
                )}
              />
            </ListItem>
            {user.id_service === 0 && (user.access_level === 0 || user.access_level === 1) && (
              <>
                <Divider/>
                <ListItem
                  sx={{
                    px: 3,
                    py: 1.5
                  }}
                >
                  <ListItemText
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      my: 0
                    }}
                    disableTypography
                    primary={
                      <Typography
                        sx={{ minWidth: 180 , mt:0.75 }}
                        variant="subtitle2"
                      >
                        Groupes
                      </Typography>
                    }
                    secondary={
                      <Box
                        sx={{
                          flex: 1,
                          mt: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: 'wrap'
                        }}
                      >
                        {userGroups.length > 0 ? (
  userGroups.map(group => (
    <Chip 
      key={group.id}
      label={group.nom} 
      color="primary"
      sx={{ mb: 1, cursor: 'pointer' }}
      onClick={() => {
        const tenant = localStorage.getItem('tenant');
        navigate(`/${tenant}/dashboard/group/${group.id}`);
      }}
    />
  ))
) : (
  <Typography color="textSecondary">Aucun groupe</Typography>
)}
                        <Button 
                          size="small" 
                          onClick={() => setOpenGroupDialog(true)}
                          sx={{ ml: 1 , mt:-1 }}
                        >
                          Modifier
                        </Button>
                      </Box>
                    }
                  />
                </ListItem>
                {user.id_service === 0 && (user.access_level === 0 ||user.access_level === 1) &&<> <Divider/>
            <ListItem
              sx={{
                px: 3,
                py: 1.5
              }}
            >
              <ListItemText
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  my: 0
                }}
                disableTypography
                primary={
                  <Typography
                    sx={{ minWidth: 180}}
                    variant="subtitle2"
                  >
                    Relance automatique
                  </Typography>
                }
                secondary={(
                  <Box
                    sx={{
                      flex: 1,
                      mt: 0,
                      display: "flex",
                      maxWidth: 1000
                    }}
                  >
                     <Switch
                                          inputProps={{ 'aria-label': 'controlled'}}
                                          checked={customer.details["auto_relaunch"]}
                                          // disabled={formation.used}
                                          onChange={() => {
                                            // if (formation.tlms_id) {
                                            // console.log(!customer.details["auto_relaunch"])
                                              handleModifyRelaunch(!customer.details["auto_relaunch"])
                                            // } else {
                                              // handleModifyTlms("tlms_id", -1)
                                            // }
                                          }}
                                        />
                  </Box>
                )}
              />
            </ListItem></>}
              </>
            )}
          </List>
          
          {/* Dialog pour modifier les groupes */}
          <Dialog 
            open={openGroupDialog} 
            onClose={() => setOpenGroupDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Gérer les groupes</DialogTitle>
            <DialogContent>
              {loadingGroups ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={allGroups}
                  getOptionLabel={(option) => option.nom}
                  value={selectedGroups}
                  onChange={(event, newValue) => {
                    setSelectedGroups(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sélectionner un ou plusieurs groupes"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.nom}
                        size="small"
                      />
                    ))
                  }
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenGroupDialog(false)}>Annuler</Button>
              <Button 
                onClick={handleGroupChange}
                color="primary"
                variant="contained"
                disabled={loadingGroups}
              >
                Enregistrer
              </Button>
            </DialogActions>
          </Dialog>
        </Card>
      )}
    </>
  );
};

CustomerBasicDetails.propTypes = {
  name: PropTypes.string,
  user_id: PropTypes.number,
  email: PropTypes.string,
  tlms: PropTypes.number,
  syna: PropTypes.number
};